// Founder Activation panel — Slice 1 hook. Admin-only by
// construction: admin_activation_overview() raises for non-admins,
// so a non-founder simply gets a safe empty view (the AdminOnly
// route guard already prevents render anyway). The hook is read +
// one explicit guarded action; it NEVER writes directly and cannot
// bypass any server gate — the "Activate" action routes through the
// existing activate_football_league_from_web_corpus() (admin +
// 4-condition + real-corpus enforced server-side).

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";

export interface FootballDarkLeague {
  readonly league_code: string;
  readonly league_name: string;
  readonly phase: string;
  readonly corpus_min: number;
  readonly odds_supported: boolean;
  readonly web_corpus: number;
  readonly api_corpus: number;
  /** Server-computed: web corpus >= corpus_min. The panel only
   *  enables the button when this is true (no fabricated readiness). */
  readonly activatable: boolean;
}

export interface PlannedSport {
  readonly sport_code: string;
  readonly display_name: string;
  readonly phase: string;
  readonly status: "planned" | "calibrating" | "live";
  readonly calibration_signed: boolean;
}

interface Overview {
  football_dark: FootballDarkLeague[];
  sports: PlannedSport[];
}
const EMPTY: Overview = { football_dark: [], sports: [] };

export interface UseActivationOverviewState {
  readonly footballDark: readonly FootballDarkLeague[];
  readonly sports: readonly PlannedSport[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly busyLeague: string | null;
  readonly refetch: () => Promise<void>;
  /** Activate one football league from its verified web corpus.
   *  Returns the RPC's honest result/error verbatim. */
  readonly activateFootballLeague: (
    league: string,
  ) => Promise<{ ok: boolean; message: string }>;
}

export function useActivationOverview(): UseActivationOverviewState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const [data, setData] = useState<Overview>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyLeague, setBusyLeague] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res, error: e } = await (supabase.rpc as unknown as (
        fn: string,
      ) => Promise<{ data: Overview | null; error: { message: string } | null }>)(
        "admin_activation_overview",
      );
      if (e || !res || typeof res !== "object") {
        if (e) setError(e.message);
        setData(EMPTY);
      } else {
        setData({
          football_dark: Array.isArray(res.football_dark) ? res.football_dark : [],
          sports: Array.isArray(res.sports) ? res.sports : [],
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const activateFootballLeague = useCallback(
    async (league: string): Promise<{ ok: boolean; message: string }> => {
      setBusyLeague(league);
      try {
        const { data: res, error: e } = await (supabase.rpc as unknown as (
          fn: string,
          args: Record<string, unknown>,
        ) => Promise<{ data: unknown; error: { message: string } | null }>)(
          "activate_football_league_from_web_corpus",
          { p_league: league },
        );
        if (e) return { ok: false, message: e.message };
        const ok =
          !!res && typeof res === "object" &&
          (res as { ok?: boolean }).ok === true &&
          (res as { now_live?: boolean }).now_live === true;
        await refetch();
        return {
          ok,
          message: ok
            ? `${league} is now live.`
            : `Not activated: ${JSON.stringify(res)}`,
        };
      } catch (err) {
        return { ok: false, message: err instanceof Error ? err.message : String(err) };
      } finally {
        setBusyLeague(null);
      }
    },
    [supabase, refetch],
  );

  return {
    footballDark: data.football_dark,
    sports: data.sports,
    loading,
    error,
    busyLeague,
    refetch,
    activateFootballLeague,
  };
}
